'use client';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { v4 as uuidv4 } from 'uuid';

import appConfig from '@nerdwallet/app-config';
import { AnalyticsContext } from './AnalyticsContext';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider = ({
  children,
}: AnalyticsProviderProps): JSX.Element => {
  const [pageName, setPageName] = useState<string>();
  const pageViewId = useRef<string>(uuidv4());
  const oldPageViewId = useRef(null);
  const writeKey = appConfig?.SEGMENT?.WRITE_KEY ?? '';
  const analytics = useMemo(
    () =>
      writeKey && typeof window !== 'undefined'
        ? AnalyticsBrowser.load(
            { writeKey, cdnURL: appConfig?.SEGMENT?.CDN_URL },
            {
              cookie: {
                domain: appConfig?.SEGMENT?.DOMAIN,
                maxage: appConfig?.SEGMENT?.MAX_AGE, // 10 years
                secure: appConfig?.SEGMENT?.SECURE,
              },
              integrations: {
                'Segment.io': {
                  apiHost: appConfig?.SEGMENT?.API_HOST,
                  protocol: appConfig?.SEGMENT?.PROTOCOL,
                },
              },
            },
          )
        : null,
    [writeKey],
  );

  const generateNewPageViewId = useCallback(() => {
    const newPageViewId = uuidv4();
    oldPageViewId.current = pageViewId.current;
    pageViewId.current = newPageViewId;
    return { newPageViewId, previousPageViewId: oldPageViewId.current };
  }, []);

  const updatePageViewId = useCallback((newPageViewId: string) => {
    oldPageViewId.current = pageViewId.current;
    pageViewId.current = newPageViewId;
    return { newPageViewId, previousPageViewId: oldPageViewId.current };
  }, []);

  const updatePageName = useCallback((newPageName: string) => {
    setPageName(newPageName);
    return newPageName;
  }, []);

  return (
    <AnalyticsContext.Provider
      value={{
        analytics,
        pageViewId: pageViewId.current,
        oldPageViewId: oldPageViewId.current,
        pageName,
        generateNewPageViewId,
        updatePageName,
        updatePageViewId,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
